import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { paymentApi } from '@/api/payment';
import { ImportCurve, DocumentText, Spinner } from '@/components/icons';
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableHeaderRow,
    DataTableRow,
    ListSection,
} from '@/components/system';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export function InvoicesTable() {
    const { data: invoices, isLoading } = useQuery({
        queryKey: ['invoices'],
        queryFn: paymentApi.getInvoices,
    });

    const formatCurrency = (amount: number, currency: string) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: currency,
        }).format(amount / 100);
    };

    const getStatusBadge = (status: string) => {
        const statusMap: Record<
            string,
            {
                label: string;
                variant: 'success' | 'warning' | 'neutral' | 'destructive';
            }
        > = {
            paid: { label: 'Pago', variant: 'success' },
            open: { label: 'Aberto', variant: 'warning' },
            void: { label: 'Cancelado', variant: 'neutral' },
            draft: { label: 'Rascunho', variant: 'neutral' },
            uncollectible: { label: 'Incobrável', variant: 'destructive' },
        };

        const config = statusMap[status.toLowerCase()] || {
            label: status,
            variant: 'neutral' as const,
        };

        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    if (isLoading) {
        return (
            <ListSection
                title="Histórico de Faturas"
                contentClassName="p-6"
                controls={
                    <div className="flex size-10 items-center justify-center rounded-xl border border-border bg-surface-muted text-muted-foreground">
                        <DocumentText className="size-5" />
                    </div>
                }
            >
                <div className="flex justify-center py-8">
                    <Spinner className="size-8 animate-spin text-muted-foreground" />
                </div>
            </ListSection>
        );
    }

    if (!invoices || invoices.length === 0) {
        return null;
    }

    return (
        <ListSection
            title="Histórico de Faturas"
            contentClassName="p-0"
            controls={
                <div className="flex size-10 items-center justify-center rounded-xl border border-border bg-surface-muted text-muted-foreground">
                    <DocumentText className="size-5" />
                </div>
            }
        >
            <DataTable>
                <DataTableHeader>
                    <DataTableHeaderRow>
                        <DataTableHead>Data</DataTableHead>
                        <DataTableHead>Período</DataTableHead>
                        <DataTableHead>Valor</DataTableHead>
                        <DataTableHead>Status</DataTableHead>
                        <DataTableHead className="text-right">Fatura</DataTableHead>
                    </DataTableHeaderRow>
                </DataTableHeader>
                <DataTableBody>
                    {invoices.map((invoice) => (
                        <DataTableRow key={invoice.id}>
                            <DataTableCell>
                                <span className="font-medium text-foreground">
                                    {format(new Date(invoice.created_at), 'dd/MM/yyyy', {
                                        locale: ptBR,
                                    })}
                                </span>
                            </DataTableCell>
                            <DataTableCell variant="subtle">
                                {format(new Date(invoice.period_start), 'dd/MM', {
                                    locale: ptBR,
                                })}{' '}
                                -{' '}
                                {format(new Date(invoice.period_end), 'dd/MM/yyyy', {
                                    locale: ptBR,
                                })}
                            </DataTableCell>
                            <DataTableCell>
                                <span className="font-semibold text-foreground">
                                    {formatCurrency(invoice.amount_total, invoice.currency)}
                                </span>
                            </DataTableCell>
                            <DataTableCell>{getStatusBadge(invoice.status)}</DataTableCell>
                            <DataTableCell className="text-right">
                                {invoice.invoice_pdf ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="size-8 p-0 transition-colors hover:bg-brand/10 hover:text-brand"
                                        asChild
                                    >
                                        <a
                                            href={invoice.invoice_pdf}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Baixar Fatura"
                                        >
                                            <ImportCurve className="size-4" />
                                        </a>
                                    </Button>
                                ) : (
                                    <span className="text-muted-foreground">-</span>
                                )}
                            </DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </ListSection>
    );
}
